import Image from 'app/Components/Shared/Image/image';
import React, { useCallback, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import { setPopUp, setSelectedSong as setSelectedSongAction, getSubscribersData } from '../../FuntoneSlice';
import { useScreenNavigation } from '../../Utils/ScreenNavigationUtils';
import { audioPlayer } from '../../Utils/AudioPlayer';
import './SongCard.scss';
import playIcon from './images/play.svg';
import pauseIcon from './images/pause.svg';
import downloadIcon from './images/download.svg';
import menuIcon from './images/menu.svg';
import waveformIcon from './images/Waveform.png';
import SongCardMenu from './SongCardMenu';

import addIcon from './images/add-to-playlist.svg';
import removeIcon from './images/remove-from-playlist.svg';

const SongCard = ({
  song = {},
  onDownload,
  loginData = {},
  setPopUp,
  setSelectedSong,
  subscribersData,
  isSubscribersFetching,
  fetchSubscribersData,
  showMenu = false,
  menuActions = [],
  headerTitle = '',
  inTileView = false, // New prop to indicate if card is in SongsTile
  disableHover = false, // New prop to disable hover effects
  expandButton = null, // New prop for expand button component
  onClick = null, // New prop for card click handler
}) => {
  const hasActions = song?.isDownloadable || song?.isPlayable;
  const [menuOpen, setMenuOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const menuAnchorRef = useRef(null);

  const { navigateToSelectSubscriber } = useScreenNavigation();

  // Ref that stores the continueDownload callback to call once subscribersData arrives
  const pendingNavigateRef = useRef(null);

  // When subscribersData transitions from null/absent to populated, fire any pending navigation
  useEffect(() => {
    if (subscribersData && pendingNavigateRef.current) {
      const callback = pendingNavigateRef.current;
      pendingNavigateRef.current = null;
      navigateToSelectSubscriber({ onAfterNavigate: callback });
    }
  }, [subscribersData, navigateToSelectSubscriber]);

  const songId = song?.id || song?.productId || song?.songId;
  const previewUrl = song?.funtonPrevUrl || song?.previewUrl;
  const hasPreviewUrl = previewUrl && previewUrl.trim() !== '';

  // Subscribe to audio player updates
  useEffect(() => {
    const unsubscribe = audioPlayer.subscribe((playingSongId, playing) => {
      if (playingSongId === songId) {
        setIsPlaying(playing);
      } else if (isPlaying) {
        setIsPlaying(false);
      }
    });

    // Initialize playing state
    setIsPlaying(audioPlayer.isPlaying(songId));

    return unsubscribe;
  }, [songId, isPlaying]);

  const {
    isFullAuthenticated = false,
    isOtpAuthenticated = false,
    isOtpExtendedAuthenticated = false,
    isSilentOtpAuthenticated = false,
    userDetails = {},
  } = loginData || {};

  const { IsContactPerson = false, IsPrimaryContact = false } = userDetails || {};

  const handlePlayClick = useCallback(
    (e) => {
      e.stopPropagation();

      if (!previewUrl) {
        console.warn('No preview URL available for song:', songId);
        return;
      }

      if (isPlaying) {
        audioPlayer.pause();
      } else {
        audioPlayer.play(songId, previewUrl);
      }
    },
    [previewUrl, songId, isPlaying],
  );

  const handleDownloadClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (setSelectedSong) setSelectedSong(song);

      const continueDownload = () => {
        if (typeof onDownload === 'function') onDownload(song);
        else if (typeof song.handleDownload === 'function') song.handleDownload(song);
        else if (typeof song.onDownload === 'function') song.onDownload(song);
      };

      if (isFullAuthenticated || isOtpAuthenticated || isOtpExtendedAuthenticated || isSilentOtpAuthenticated) {
        if (subscribersData) {
          // Data already loaded — navigate immediately
          navigateToSelectSubscriber({ onAfterNavigate: continueDownload });
        } else if (!isSubscribersFetching) {
          // No data and no in-flight call — fetch then navigate
          pendingNavigateRef.current = continueDownload;
          fetchSubscribersData();
        } else {
          // Fetch already in-flight — wait for the useEffect above to fire
          pendingNavigateRef.current = continueDownload;
        }
        return;
      }

      setPopUp &&
        setPopUp({
          type: 'LOGIN',
          stage: 'PHONE',
          data: { intent: { type: 'NAVIGATE_TO_SELECT_SUBSCRIBER', songId: song?.id || song?.songId || null } },
        });
    },
    [
      song,
      onDownload,
      isFullAuthenticated,
      isOtpAuthenticated,
      isOtpExtendedAuthenticated,
      isSilentOtpAuthenticated,
      subscribersData,
      isSubscribersFetching,
      fetchSubscribersData,
      navigateToSelectSubscriber,
      setPopUp,
      setSelectedSong,
    ],
  );

  function isRTL(text) {
    return /[\u0590-\u05FF]/.test(text);
  }

  return (
    <div
      className={`song-card ${hasActions ? 'has-actions' : ''} ${inTileView ? 'in-tile-view' : ''} ${menuOpen ? 'menu-open' : ''} ${disableHover ? 'disable-hover' : ''} ${expandButton ? 'has-expand-button' : ''}`}
      onClick={onClick}
    >
      {showMenu && (
        <div
          className='song-card-menu-anchor'
          ref={menuAnchorRef}
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen(!menuOpen);
          }}>
          <Image src={menuIcon} alt='menu' />
        </div>
      )}
      <div className='song-image-container'>
        {song.image && <Image src={song.image} alt={song.title} className='song-image' />}
        {isPlaying && (
          <div className='song-waveform-overlay'>
            <Image src={waveformIcon} alt='Playing' className='waveform-icon' />
          </div>
        )}
      </div>

      <div className='song-info'>
        {song.description && <span
          className={`song-title${isRTL(song.description) ? ' rtl' : ' ltr'}`}
          dir={isRTL(song.description) ? 'rtl' : 'ltr'}
        >
          {song.description}
        </span>}
        {song.author && (
          <span
            className={`song-artist${isRTL(song.author) ? ' rtl' : ' ltr'}`}
            dir={isRTL(song.author) ? 'rtl' : 'ltr'}
          >
            {song.author}
          </span>
        )}
      </div>

      <div className="song-actions">
        {song?.isPlayable && hasPreviewUrl && (
          <button type='button' className='play-pause-button' aria-label={isPlaying ? 'Pause' : 'Play'} onClick={handlePlayClick}>
            <Image src={isPlaying ? pauseIcon : playIcon} alt={isPlaying ? 'Pause' : 'Play'} />
          </button>
        )}
        {song?.isDownloadable && (
          <button type='button' className='download-button' aria-label='Download' onClick={handleDownloadClick}>
            <Image src={downloadIcon} alt='' />
          </button>
        )}
        {expandButton && (
          <div className="expand-button-container">
            {expandButton}
          </div>
        )}
      </div>

      {menuOpen && showMenu && createPortal(
        <SongCardMenu actions={menuActions} anchorRef={menuAnchorRef} onClose={() => setMenuOpen(false)} />,
        document.body
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    loginData: state.authentication.loginData,
    subscribersData: state.FuntoneSlice.subscribersData,
    isSubscribersFetching: state.FuntoneSlice.isSubscribersFetching,
  };
};

const mapDispatchToProps = (dispatch) => ({
  setSelectedSong: (song) => dispatch(setSelectedSongAction(song)),
  setPopUp: (data) => dispatch(setPopUp(data)),
  fetchSubscribersData: () => dispatch(getSubscribersData()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SongCard);
